/**
 * WheelLottery - 大转盘抽奖组件
 *
 * 用于营销活动的交互式抽奖转盘，支持多品牌主题和响应式布局。
 *
 * @module WheelLottery
 * @date 2025-12-17
 */

'use client'

import { useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import { cn } from '../../helpers/index.js'
import { DEFAULT_WHEEL_CONFIG, DEFAULT_CHANCE_METHODS, INITIAL_WHEEL_STATE } from './types.js'
import { Grid, GridItem } from '../../components/grid.js'
import { Wheel } from './Wheel.js'
import { PrizePool } from '../LotteryShared/PrizePool.js'
import { ChanceMethods } from '../LotteryShared/ChanceMethods.js'
import { WinnerModal } from '../LotteryShared/WinnerModal.js'
import { RulesModal } from '../LotteryShared/RulesModal.js'
import { MyRewardsModal } from '../LotteryShared/MyRewardsModal.js'
import { ErrorModal } from '../LotteryShared/ErrorModal.js'
import { ShareModal } from '../LotteryShared/ShareModal.js'
import { withLayout } from '../../shared/Styles.js'

import type { WheelLotteryProps, WheelState, WheelLotteryHandle, WinnerModalConfig } from './types.js'

// ============================================================================
// 主组件
// ============================================================================

/**
 * WheelLottery 组件
 *
 * @example
 * ```tsx
 * // 基础使用
 * <WheelLottery
 *   prizes={prizes}
 *   winningPrizeId="prize-003"
 *   onSpinEnd={(prize) => alert(`You won ${prize.name}`)}
 * />
 *
 * // 使用 ref 控制弹窗
 * const lotteryRef = useRef<WheelLotteryHandle>(null)
 *
 * <WheelLottery ref={lotteryRef} prizes={prizes} />
 *
 * // 外部触发弹窗
 * lotteryRef.current?.showError({
 *   title: 'Error',
 *   message: 'Something went wrong'
 * })
 * ```
 */
export const WheelLottery = forwardRef<WheelLotteryHandle, WheelLotteryProps>(
  (
    {
      prizes,
      theme,
      winningPrizeId,
      spinDuration = DEFAULT_WHEEL_CONFIG.spinDuration,
      wheelBackgroundImage,
      pointerImage,
      chanceMethods = DEFAULT_CHANCE_METHODS,
      chanceTitle = DEFAULT_WHEEL_CONFIG.chanceTitle,
      chanceSubtitle,
      chanceFooterNote,
      chanceMethodsText,
      mechanismType,
      inputConfig,
      winningInfos = [],
      prizesTitle,
      onSpinStart,
      onSpinEnd,
      onSpinError,
      winnerModalConfig,
      rulesModalConfig = {},
      myRewardsModalConfig = {},
      errorModalConfig = {},
      shareModalConfig = {},
      userData,
      loginPromptText = 'Please log in to participate',
      loginButtonText = 'Log In',
      noChancesText = 'You have no chances left',
      onLoginRequired,
      onCopyCode,
      opportunitiesText = ['Number of draws remaining today:'],
      className,
    },
    ref
  ) => {
    const { rulesData, rulesText = 'Rules' } = rulesModalConfig
    const {
      rewardsData,
      myRewardsText = 'My Rewards',
      codeText = 'CODE:',
      copyText = 'COPY',
      copiedText = 'COPIED',
      prizeText = 'Prize time:',
    } = myRewardsModalConfig
    const completedText = chanceMethodsText?.completedText
    const usedText = chanceMethodsText?.usedText
    const isLoggedIn = userData?.isLoggedIn ?? false
    const availableChances = userData?.availableChances || 0
    const showLoginOverlay = !isLoggedIn
    const showNoChancesOverlay = isLoggedIn && availableChances <= 0
    // ==========================================================================
    // State 管理
    // ==========================================================================

    const [wheelState, setWheelState] = useState<WheelState>(INITIAL_WHEEL_STATE)
    const [showWinnerModal, setShowWinnerModal] = useState(false)
    const [showRulesModal, setShowRulesModal] = useState(false)
    const [showRewardsModal, setShowRewardsModal] = useState(false)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [showNoWinModal, setShowNoWinModal] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [errorTitle, setErrorTitle] = useState<string | undefined>()
    const [errorConfirmText, setErrorConfirmText] = useState<string | undefined>()
    const [noWinTitle, setNoWinTitle] = useState<string | undefined>()
    const [noWinMessage, setNoWinMessage] = useState<string | undefined>()
    const [noWinConfirmText, setNoWinConfirmText] = useState<string | undefined>()
    const [externalWinnerConfig, setExternalWinnerConfig] = useState<Partial<WinnerModalConfig> | undefined>()
    const animationRef = useRef<number | null>(null)

    // ==========================================================================
    // Imperative Handle - 暴露方法给父组件
    // ==========================================================================

    useImperativeHandle(
      ref,
      () => ({
        showError: ({ message, title, confirmText }) => {
          setErrorMessage(message)
          setErrorTitle(title)
          setErrorConfirmText(confirmText)
          setShowErrorModal(true)
        },
        hideError: () => {
          setShowErrorModal(false)
        },
        showNoWin: config => {
          setNoWinTitle(config?.title)
          setNoWinMessage(config?.message)
          setNoWinConfirmText(config?.confirmText)
          setShowNoWinModal(true)
        },
        hideNoWin: () => {
          setShowNoWinModal(false)
        },
        showWinner: (prize, config) => {
          setWheelState(prev => ({ ...prev, winningPrize: prize }))
          setExternalWinnerConfig(config)
          setShowWinnerModal(true)
        },
        hideWinner: () => {
          setShowWinnerModal(false)
        },
        showRules: () => {
          setShowRulesModal(true)
        },
        hideRules: () => {
          setShowRulesModal(false)
        },
        showRewards: () => {
          setShowRewardsModal(true)
        },
        hideRewards: () => {
          setShowRewardsModal(false)
        },
        showShare: () => {
          setShowShareModal(true)
        },
        hideShare: () => {
          setShowShareModal(false)
        },
        hideAllModals: () => {
          setShowWinnerModal(false)
          setShowRulesModal(false)
          setShowRewardsModal(false)
          setShowErrorModal(false)
          setShowNoWinModal(false)
          setShowShareModal(false)
        },
      }),
      []
    )

    // ==========================================================================
    // Props 验证（开发环境）
    // ==========================================================================

    useEffect(() => {
      if (process.env.NODE_ENV === 'development' && prizes) {
        if (prizes.length !== 8) {
          console.warn(
            `WheelLottery expects exactly 8 prizes, got ${prizes.length}. The component may not render correctly.`
          )
        }

        if (winningPrizeId && !prizes.find(p => p.prizeKey === winningPrizeId)) {
          console.warn(`WheelLottery: winningPrizeId "${winningPrizeId}" not found in prizes array.`)
        }
      }
    }, [prizes, winningPrizeId])

    // ==========================================================================
    // 清理动画
    // ==========================================================================

    useEffect(() => {
      return () => {
        // 组件卸载时取消动画帧
        if (animationRef.current !== null) {
          cancelAnimationFrame(animationRef.current)
        }
      }
    }, [])

    // ==========================================================================
    // 抽奖逻辑
    // ==========================================================================

    /**
     * 开始抽奖 - 支持异步接口调用
     * 利用初始慢速旋转，点击后进入快速旋转状态，接口返回后减速停止
     */
    const handleSpinStart = useCallback(async () => {
      if (wheelState.isSpinning) return

      // 检查用户是否已登录
      if (!userData?.isLoggedIn) {
        onLoginRequired?.()
        return
      }

      // ✅ 次数为 0 时，按钮已被禁用，这里不需要额外检查
      // 防御性编程：如果仍然触发，直接返回
      if (userData.availableChances <= 0) {
        return
      }

      // 进入快速旋转状态（初始慢速旋转会自动变为快速旋转）
      setWheelState(prev => ({
        ...prev,
        isSpinning: true,
        isButtonDisabled: true,
      }))

      try {
        // 调用外部接口获取中奖结果
        let finalWinningPrizeId = winningPrizeId

        if (onSpinStart) {
          // 等待接口返回中奖 ID（接口时间不固定，1-30s）
          // 在此期间，转盘会保持快速旋转（由 CSS 动画控制）
          finalWinningPrizeId = await onSpinStart()
        }

        // 确定中奖奖品
        const winningPrize = prizes.find(p => p.prizeKey === finalWinningPrizeId) || prizes[0]
        const winningIndex = prizes.findIndex(p => p.prizeKey === winningPrize.prizeKey)

        // 计算目标角度
        const degreesPerPrize = 360 / prizes.length
        const basePrizeAngle = winningIndex * degreesPerPrize
        // 至少旋转 3 圈（1080度）+ 目标角度
        const minRotations = 3
        const targetAngle = minRotations * 360 + (360 - basePrizeAngle)

        // 接口返回后，等待 1.5s 再开始停止动画
        await new Promise(resolve => setTimeout(resolve, 1500))

        // 立即进入减速状态，停止 CSS 循环动画
        setWheelState(prev => ({
          ...prev,
          isFinished: true,
          currentRotation: 0, // 从 0 度开始减速动画
        }))

        // 开始停止旋转动画
        const startTime = Date.now()

        const animate = () => {
          const now = Date.now()
          const elapsed = now - startTime
          const progress = Math.min(elapsed / spinDuration, 1)

          // 应用 ease-out 缓动函数
          const eased = 1 - Math.pow(1 - progress, 3)

          // 计算当前旋转角度
          const currentRotation = targetAngle * eased

          // 更新状态
          setWheelState(prev => ({
            ...prev,
            currentRotation,
          }))

          // 继续动画或结束
          if (progress < 1) {
            animationRef.current = requestAnimationFrame(animate)
          } else {
            // 动画结束 - 保持按钮禁用状态,直到弹窗关闭
            setWheelState(prev => ({
              ...prev,
              isSpinning: false,
              isButtonDisabled: true,
              winningPrize,
              currentRotation: targetAngle,
            }))

            // 延迟显示弹窗（500ms后）
            if (onSpinEnd) {
              onSpinEnd?.(winningPrize)
            } else {
              setTimeout(() => {
                // 检查是否未中奖（try-again）
                if (winningPrize.prizeKey === 'try-again') {
                  // 未中奖，显示 ErrorModal
                  setShowNoWinModal(true)
                } else {
                  // 中奖，显示 WinnerModal
                  setShowWinnerModal(true)
                }
              }, 500)
            }
          }
        }

        // 启动停止动画
        animationRef.current = requestAnimationFrame(animate)
      } catch (error) {
        // 接口报错，恢复到初始慢速旋转状态
        setWheelState(prev => ({
          ...prev,
          isSpinning: false,
          isButtonDisabled: false,
        }))

        // 显示错误弹窗
        setErrorMessage((error as Error).message || 'An error occurred')
        setShowErrorModal(true)

        // 触发错误回调
        onSpinError?.(error as Error)

        console.error('抽奖接口调用失败:', error)
      }
    }, [
      wheelState.isSpinning,
      prizes,
      winningPrizeId,
      spinDuration,
      onSpinStart,
      onSpinEnd,
      onSpinError,
      userData,
      onLoginRequired,
    ])

    const handleWheelGoClick = useCallback(() => {
      if (isLoggedIn) {
        void handleSpinStart()
        return
      }

      onLoginRequired?.()
    }, [handleSpinStart, isLoggedIn, onLoginRequired])

    // ==========================================================================
    // 弹窗关闭处理
    // ==========================================================================

    /**
     * 关闭中奖弹窗并重置转盘状态
     */
    const handleCloseWinnerModal = useCallback(() => {
      setShowWinnerModal(false)
      // 重置转盘状态,允许下次抽奖
      setWheelState(prev => ({
        ...prev,
        isButtonDisabled: false,
        isFinished: false, // 重置为未完成,恢复初始旋转动画
      }))
    }, [])

    /**
     * 关闭未中奖弹窗并重置转盘状态
     */
    const handleCloseNoWinModal = useCallback(() => {
      setShowNoWinModal(false)
      // 重置转盘状态,允许下次抽奖
      setWheelState(prev => ({
        ...prev,
        isButtonDisabled: false,
        isFinished: false, // 重置为未完成,恢复初始旋转动画
      }))
    }, [])

    // ==========================================================================
    // 渲染
    // ==========================================================================

    return (
      <div className={cn('wheel-lottery-container', 'w-full', theme === 'dark' ? 'aiui-dark' : '', className)}>
        {/* ARIA Live Region - 屏幕阅读器状态播报 */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {wheelState.isSpinning && '抽奖进行中...'}
          {wheelState.winningPrize && !wheelState.isSpinning && `已中奖: ${wheelState.winningPrize.name}`}
        </div>

        {/* 主内容区 - 响应式两栏/单栏布局 */}
        <Grid className={cn('wheel-lottery-main', 'tablet:mb-4 mb-3')}>
          {/* 左侧：转盘区域 */}
          <GridItem
            span={7}
            className={cn(
              'wheel-section',
              'relative flex w-full flex-1 items-center justify-center overflow-hidden',
              'rounded-box',
              // 移动端（<=1024px）使用min-h，桌面端使用aspect ratio
              'laptop:min-h-[288px] min-h-[240px]',
              'lg-desktop:aspect-w-[964] lg-desktop:aspect-h-[480]',
              'desktop:aspect-w-[759] desktop:aspect-h-[384]',
              'laptop:aspect-w-[516] laptop:aspect-h-[288]',
              'l-tablet:col-span-12',
              // 移动端底部间距
              'l-tablet:mb-3',
              theme === 'dark' ? 'bg-[#1E2024]' : 'bg-[#EAEAEC]'
            )}
          >
            <div
              className={cn(
                'w-full px-[16px] pt-[16px]',
                'lg-desktop:pb-[32px] lg-desktop:px-[32px]',
                'desktop:pt-[24px] desktop:px-[24px]',
                'laptop:pt-[16px] laptop:px-[16px]',
                'tablet:px-[24px]',
                // 移动端(<=1024px)时使用绝对定位覆盖在转盘背景上
                'l-tablet:absolute l-tablet:top-0 l-tablet:inset-x-0 l-tablet:z-10'
              )}
            >
              {/* 顶部信息栏：左侧机会进度，右侧链接 */}
              <div className="relative z-10 mb-2 flex max-h-[24px] w-full flex-wrap items-center justify-between gap-3">
                {/* 左侧：机会展示 - 仅登录时显示 */}
                <div>
                  {isLoggedIn && (
                    <p className="text-info-primary font-[700]">
                      {opportunitiesText?.[0]}
                      <span className="ml-1 text-[#FF6B35]">{availableChances}</span>
                      {opportunitiesText?.[1] || ''}
                    </p>
                  )}
                </div>
                {/* 右侧：Rules 和 My Rewards 按钮 */}
                <div className="flex items-center gap-4">
                  {rulesData && (
                    <button
                      onClick={() => setShowRulesModal(true)}
                      className="cursor-pointer touch-manipulation"
                      type="button"
                    >
                      <span className="text-info-primary font-[700] underline">{rulesText}</span>
                    </button>
                  )}
                  {/* My Rewards 按钮 - 仅登录时显示 */}
                  {userData?.isLoggedIn && rewardsData && (
                    <button
                      onClick={() => setShowRewardsModal(true)}
                      className="cursor-pointer touch-manipulation whitespace-nowrap"
                      type="button"
                    >
                      <span className="text-info-primary font-[700] underline">{myRewardsText}</span>
                    </button>
                  )}
                </div>
              </div>
              {/* 转盘组件 */}
              <Wheel
                theme={theme}
                prizes={prizes}
                rotation={wheelState.currentRotation}
                isSpinning={wheelState.isSpinning}
                isFinished={wheelState.isFinished}
                isButtonDisabled={wheelState.isButtonDisabled || !isLoggedIn || showNoChancesOverlay}
                userData={userData}
                onGoClick={handleWheelGoClick}
                wheelBackgroundImage={wheelBackgroundImage}
                pointerImage={pointerImage}
              />

              {(showLoginOverlay || showNoChancesOverlay) && (
                <div
                  className={cn(
                    'rounded-box absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm',
                    'px-6 text-center'
                  )}
                >
                  <div className="flex flex-col items-center gap-4">
                    <p className="lg-desktop:text-[24px] text-[18px] font-semibold text-white">
                      {showLoginOverlay ? loginPromptText : noChancesText}
                    </p>
                    {showLoginOverlay && (
                      <button
                        type="button"
                        onClick={onLoginRequired}
                        className={cn(
                          'rounded-full bg-white px-6 py-2 text-[14px] font-semibold text-[#1D1D1F]',
                          'transition-transform hover:scale-105 active:scale-95'
                        )}
                      >
                        {loginButtonText}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </GridItem>

          {/* 右侧：获取机会区域 */}
          {chanceMethods.length > 0 && (
            <GridItem
              span={5}
              className={cn(
                'info-section',
                'rounded-box',
                'relative flex flex-col overflow-hidden',
                'lg-desktop:aspect-w-[684] lg-desktop:aspect-h-[480]',
                'desktop:aspect-w-[536] desktop:aspect-h-[384]',
                'laptop:aspect-w-[364] laptop:aspect-h-[288]',
                // 移动端使用min-h而不是max-h，确保内容完整显示
                'l-tablet:min-h-[280px] laptop:max-h-[320px]',
                'w-full',
                'l-tablet:col-span-12',
                theme === 'dark' ? 'bg-[#1E2024]' : 'bg-[#EAEAEC]'
              )}
            >
              <ChanceMethods
                theme={theme}
                userData={userData}
                methods={chanceMethods}
                title={chanceTitle}
                subtitle={chanceSubtitle}
                footerNote={chanceFooterNote}
                onOpenShareModal={() => setShowShareModal(true)}
                chanceBadgeText={chanceMethodsText?.chanceBadgeText}
                completedText={completedText}
                usedText={usedText}
                loadingText={chanceMethodsText?.loadingText}
                mechanismType={mechanismType}
                inputConfig={inputConfig}
              />
            </GridItem>
          )}
        </Grid>

        {/* 底部区域：奖品池(包含中奖人名单) */}
        <div
          className={cn(
            'wheel-lottery-footer',
            'rounded-box',
            'flex flex-col overflow-hidden',
            theme === 'dark' ? 'bg-[#1E2024]' : 'bg-[#EAEAEC]'
          )}
        >
          {/* 奖品池(包含中奖人名单自动滚动) */}
          <PrizePool theme={theme} prizes={prizes} title={prizesTitle} winningInfos={winningInfos} />
        </div>

        {/* 中奖弹窗 */}
        <WinnerModal
          isOpen={showWinnerModal}
          prize={wheelState.winningPrize}
          title={externalWinnerConfig?.title || winnerModalConfig?.title}
          prizeTitle={externalWinnerConfig?.prizeTitle || winnerModalConfig?.prizeTitle}
          prizeImage={externalWinnerConfig?.prizeImage || winnerModalConfig?.prizeImage}
          prizeDescription={externalWinnerConfig?.prizeDescription || winnerModalConfig?.prizeDescription}
          learnMoreUrl={externalWinnerConfig?.learnMoreUrl || winnerModalConfig?.learnMoreUrl}
          learnMoreText={externalWinnerConfig?.learnMoreText || winnerModalConfig?.learnMoreText}
          couponCode={externalWinnerConfig?.couponCode || winnerModalConfig?.couponCode}
          expiresAt={externalWinnerConfig?.expiresAt || winnerModalConfig?.expiresAt}
          couponDiscount={externalWinnerConfig?.couponDiscount || winnerModalConfig?.couponDiscount}
          couponUnit={externalWinnerConfig?.couponUnit || winnerModalConfig?.couponUnit}
          confirmText={externalWinnerConfig?.confirmText || winnerModalConfig?.confirmText}
          onClose={handleCloseWinnerModal}
          theme={theme}
        />

        {/* Rules 弹窗 */}
        {rulesData && (
          <RulesModal
            title={rulesText}
            isOpen={showRulesModal}
            onClose={() => setShowRulesModal(false)}
            rules={rulesData}
            theme={theme}
          />
        )}

        {/* My Rewards 弹窗 */}
        {rewardsData && (
          <MyRewardsModal
            isOpen={showRewardsModal}
            onClose={() => setShowRewardsModal(false)}
            rewards={rewardsData}
            onCopyCode={onCopyCode}
            codeText={codeText}
            copyText={copyText}
            copiedText={copiedText}
            prizeText={prizeText}
            theme={theme}
            title={myRewardsText}
          />
        )}

        {/* 错误弹窗 */}
        <ErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={errorTitle || errorModalConfig?.title}
          message={errorMessage || errorModalConfig?.message}
          confirmText={errorConfirmText || errorModalConfig?.confirmText}
          theme={theme}
        />

        {/* 未中奖弹窗 */}
        <ErrorModal
          isOpen={showNoWinModal}
          onClose={handleCloseNoWinModal}
          title={noWinTitle || errorModalConfig?.title}
          message={noWinMessage || errorModalConfig?.message}
          confirmText={noWinConfirmText || errorModalConfig?.confirmText}
          theme={theme}
        />

        {/* 分享弹窗 */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={shareModalConfig?.title}
          subtitle={shareModalConfig?.subtitle}
          note={shareModalConfig?.note}
          platforms={shareModalConfig?.platforms}
          onShareSuccess={shareModalConfig?.onShareSuccess}
          theme={theme}
        />
      </div>
    )
  }
)

// 设置 displayName 用于调试
WheelLottery.displayName = 'WheelLottery'

// ============================================================================
// 导出
// ============================================================================

export default withLayout(WheelLottery)
export { WinnerModal } from '../LotteryShared/WinnerModal.js'
export { RulesModal } from '../LotteryShared/RulesModal.js'
export { MyRewardsModal } from '../LotteryShared/MyRewardsModal.js'
export { ErrorModal } from '../LotteryShared/ErrorModal.js'
export { ShareModal } from '../LotteryShared/ShareModal.js'
export { BaseModal } from '../LotteryShared/BaseModal.js'
export type {
  WheelLotteryProps,
  WheelLotteryHandle,
  Prize,
  ChanceMethod,
  ChanceMethodStatus,
  WinningInfo,
  WheelProps,
  PrizePoolProps,
  ChanceMethodsProps,
  WinnerModalProps,
  WinnerModalConfig,
  UserData,
  ShareModalConfig,
  SocialPlatform,
  SharePlatformConfig,
} from './types.js'
export type { RulesModalProps, RuleItem } from '../LotteryShared/RulesModal.js'
export type { MyRewardsModalProps, Reward, RewardStatus } from '../LotteryShared/MyRewardsModal.js'
export type { ErrorModalProps } from '../LotteryShared/ErrorModal.js'
export type { ShareModalProps } from '../LotteryShared/ShareModal.js'
export type { BaseModalProps } from '../LotteryShared/BaseModal.js'
